export interface ProjectEvidencePreparationResult {
    project: Record<string, any>;
    changed: boolean;
}

export interface ProjectEvidencePreparationOptions {
    project: Record<string, any>;
    selectedFileIds?: string[] | null;
    apiKey?: string;
    model?: string;
    audioModel?: string;
    signal?: AbortSignal;
    assertCurrent: () => void;
    onProgress?: (progress: number, message: string) => void;
    /**
     * Full AI ignores an uploaded file it cannot read (Peter, 2026-10-22: "If
     * photos or other uploaded files are corrupt or 0 bytes or whatever, just
     * ignore those files and continue if you can"): a PDF or video with no
     * bytes, empty, or unreadable is set aside with a recorded reason instead of
     * stopping preparation. The Quote Run does the same (2026-10-23).
     */
    setAsideUnreadableFiles?: boolean;
    /** Caller persists with its project/load-generation guards, then returns the saved row. */
    onEvidencePrepared?: (result: ProjectEvidencePreparationResult) => Promise<Record<string, any> | void>;
}

export function assertEvidencePreparationCurrent(options: ProjectEvidencePreparationOptions): void {
    if (options.signal?.aborted) {
        throw Object.assign(new Error('Project evidence preparation cancelled.'), { name: 'AbortError' });
    }
    options.assertCurrent();
}

export async function checkpointPreparedEvidence(
    options: ProjectEvidencePreparationOptions, project: Record<string, any>,
): Promise<Record<string, any>> {
    assertEvidencePreparationCurrent(options);
    const saved = await options.onEvidencePrepared?.({ project, changed: true });
    assertEvidencePreparationCurrent(options);
    if (saved && saved.id !== project.id) {
        throw new Error('Prepared evidence checkpoint returned a different project.');
    }
    return saved || project;
}
